import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, switchMap } from 'rxjs';
import { FormArray, FormGroup, FormsModule } from '@angular/forms';
import { HotkeysService } from '../../global/hotkeys/hotkeys.service';
import { EditLocationService } from './service/edit-location.service';
import { Store } from '@ngrx/store';
import { filter, take, takeUntil } from 'rxjs/operators';
import { EditLocationLocalStore } from './edit-location-local-store.service';
import { UnderlineAnimation } from './edit-location-animations';
import { EditLocationActions } from '../location-list/state/location-list-actions';
import { LSCField } from '../../../models/LSCFields';
import { CustomServicesTControl } from '../../../models/locations-models/edit-location-form';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton } from '@angular/material/button';
import { OtherInformationSectionComponent } from './sections/other-information/other-information.component';
import { MediaSectionComponent } from './sections/media/media.component';
import { GeoDataSectionComponent } from './sections/geo-data/geo-data.component';
import { BusinessHoursSectionComponent } from './sections/business-hours/business-hours.component';
import { BasicInformationSectionComponent } from './sections/basic-information/basic-information.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { LetDirective } from '@ngrx/component';
import { MatDivider } from '@angular/material/divider';

export enum EditLocationSection {
  Basic = 'Basic',
  Hours = 'Hours',
  Location = 'Location',
  Media = 'Media',
  Other = 'Other',
}

@Component({
    selector: 'app-edit-location',
    templateUrl: './edit-location.component.html',
    styleUrls: ['./edit-location.component.scss'],
    animations: [UnderlineAnimation],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [EditLocationLocalStore],
    standalone: true,
  imports: [
    LetDirective,
    FormsModule,
    NgxSkeletonLoaderModule,
    BasicInformationSectionComponent,
    BusinessHoursSectionComponent,
    GeoDataSectionComponent,
    MediaSectionComponent,
    OtherInformationSectionComponent,
    MatButton,
    MatProgressSpinner,
    MatDivider,
  ],
})
export class EditLocationComponent implements OnInit, OnDestroy {

  private componentDestroyed = new Subject<void>();

  // HTML Access
  public EditLocationSection = EditLocationSection;

  constructor(private editLocationService: EditLocationService,
              public hotkeysService: HotkeysService,
              protected store: Store,
              public localStore: EditLocationLocalStore) {
  }

  ngOnInit() {
    this.registerHotkey();
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
  }

  registerHotkey() {
    this.hotkeysService.addHotkeyAndGetEventFeed('control.s', 'Save BLS location', this.componentDestroyed)
        .pipe(
            takeUntil(this.componentDestroyed),
            switchMap(() => this.localStore.state$.pipe(take(1))),
            filter(localState => !localState.saving && localState.edited),
        )
        .subscribe(() => this.attemptToSave());
  }

  /**
   * Prune data and pass on to save.
   */
  attemptToSave() {
    if (this.localStore.formGroup?.valid) {
      this.localStore.updateSaving(true);
      const locationToSave = this.editLocationService.formGroupToLocation(this.localStore.formGroup);
      this.localStore.initialLocation$.pipe(takeUntil(this.componentDestroyed), take(1))
          .subscribe(initialLocation => {
            // the location ID isn't covered by the form and has to be re-added
            locationToSave.id = initialLocation?.id;
            this.store.dispatch(EditLocationActions.editLocationData({
              location: locationToSave,
              updateCCList: true,
            }));
          });
    }
  }

  /**
   * Reset to the initial state.
   */
  undoChanges() {
    this.localStore.initializeFormGroup();
  }

  /*
    UTILS
   */

  /**
   * Iterate tab names
   * @returns EditPanelSection[]
   */
  getSectionNames() {
    return Object.keys(EditLocationSection) as EditLocationSection[];
  }

  /**
   * Smooth-scrolling to the desired section using native API.
   * @param sectionName
   */
  goToSection(sectionName: EditLocationSection): void {
    document
        .querySelector(`div[section-name="${sectionName}"]`)
        ?.scrollIntoView({ behavior: 'smooth' });
  }

  /**
   * Extracts the custom services form item.
   */
  private get customServicesFormArray() {
    return this.localStore.formGroup?.get(LSCField.customServices) as FormArray<FormGroup<CustomServicesTControl>>;
  }

  /**
   *  Adds a new custom service pair to the formArray
   */
  addCustomService() {
    this.customServicesFormArray?.push(
        this.editLocationService.createCustomServiceFormGroup(),
    );
  }

}
